import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'highlight.js/styles/github-dark.css';

import { motion } from 'framer-motion';
import {
    AlertCircle,
    BarChart2,
    Bold,
    Check,
    ChevronDown,
    Clock,
    Eye,
    FileText,
    Heading1,
    Heading2,
    Heading3,
    Image,
    Italic,
    Link,
    List,
    ListOrdered,
    LogOut,
    MessageSquare,
    Pencil,
    Plus,
    Quote,
    RefreshCw,
    Save,
    Search,
    Send,
    ShieldCheck,
    Table,
    Trash2,
    Upload,
    User,
    XCircle,
    X,
    Code,
    Minus,
} from 'lucide-react';

interface Post {
    slug: string;
    title: string;
    excerpt: string;
    content?: string;
    category: string;
    categoryName?: string;
    tags: string[];
    featured: boolean;
    published: boolean;
    pending?: boolean;
    visibility?: 'public' | 'private';
    views: number;
    date: string;
    updatedAt?: string;
}

interface TrashPost extends Post {
    deletedAt: string;
    originalSlug: string;
}

interface CategoryItem {
    slug: string;
    name: string;
    description?: string;
    color?: string;
    order?: number;
    count?: number;
    views?: number;
}

interface BlogStats {
    totalPosts: number;
    publishedPosts: number;
    totalViews: number;
    categories: number;
    featuredPosts: number;
    viewsToday: number;
}

interface ReviewComment {
    id: string;
    postSlug: string;
    parentId: string | null;
    author: string;
    content: string;
    createdAt: string;
    status: 'pending' | 'approved' | 'rejected';
}

interface LoginForm {
    username: string;
    password: string;
}

interface EditorForm {
    title: string;
    excerpt: string;
    content: string;
    category: string;
    tags: string;
    featured: boolean;
    published: boolean;
    pending: boolean; // 待发布状态
    visibility: 'public' | 'private';
    coverImage: string;
}

interface CategoryForm {
    name: string;
    slug: string;
    description: string;
    color: string;
    order: number;
}

interface ProfileForm {
    name: string;
    title: string;
    bio: string;
    avatar: string;
    location: string;
    email: string;
    website: string;
    social: {
        github: string;
        linkedin: string;
        twitter: string;
        zhihu: string;
    };
    skills: Array<{name: string; level: number; category: string}>;
}

interface ImportedContentPayload {
    title?: string;
    excerpt?: string;
    content?: string;
    category?: string;
    tags?: string[];
    featured?: boolean | null;
    published?: boolean | null;
    format?: string;
}

const EMPTY_EDITOR: EditorForm = {
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: '',
    featured: false,
    published: false,
    pending: false,
    visibility: 'public',
    coverImage: '',
};

const EMPTY_CATEGORY_FORM: CategoryForm = {
    name: '',
    slug: '',
    description: '',
    color: '#6b7280',
    order: 1,
};

const EMPTY_PROFILE_FORM: ProfileForm = {
    name: '',
    title: '',
    bio: '',
    avatar: '',
    location: '',
    email: '',
    website: '',
    social: {
        github: '',
        linkedin: '',
        twitter: '',
        zhihu: '',
    },
    skills: [],
};

const AUTOSAVE_PREFIX = 'blog-admin-autosave-v1';

const Admin: React.FC = () => {
    const [authChecking, setAuthChecking] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authError, setAuthError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginForm, setLoginForm] = useState<LoginForm>({ username: '', password: '' });

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'posts' | 'editor' | 'trash' | 'comments' | 'categories' | 'stats' | 'profile'>('posts');
    const [editorMode, setEditorMode] = useState<'markdown' | 'preview'>('markdown');
    const [codeLangOpen, setCodeLangOpen] = useState(false);
    const [editingSlug, setEditingSlug] = useState<string | null>(null);

    const [posts, setPosts] = useState<Post[]>([]);
    const [trashPosts, setTrashPosts] = useState<TrashPost[]>([]);
    const [categories, setCategories] = useState<CategoryItem[]>([]);
    const [stats, setStats] = useState<BlogStats | null>(null);
    const [reviewComments, setReviewComments] = useState<ReviewComment[]>([]);

    const [searchText, setSearchText] = useState('');
    const [trashSearchText, setTrashSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'pending' | 'draft'>('all');
    const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
    const [bulkCategory, setBulkCategory] = useState('');
    const [reviewStatus, setReviewStatus] = useState<'pending' | 'all' | 'approved' | 'rejected'>('pending');
    const [reviewSearchText, setReviewSearchText] = useState('');
    const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});

    const [formData, setFormData] = useState<EditorForm>(EMPTY_EDITOR);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [textUploading, setTextUploading] = useState(false);
    const [uploadHint, setUploadHint] = useState('');
    const [autosaveHint, setAutosaveHint] = useState('');
    const [categoryForm, setCategoryForm] = useState<CategoryForm>(EMPTY_CATEGORY_FORM);
    const [editingCategorySlug, setEditingCategorySlug] = useState<string | null>(null);
    const [categorySubmitting, setCategorySubmitting] = useState(false);
    const [profileForm, setProfileForm] = useState<ProfileForm>(EMPTY_PROFILE_FORM);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileImageUploading, setProfileImageUploading] = useState(false);
    const [profileImageHint, setProfileImageHint] = useState('');
    const editorTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    const imageInputRef = useRef<HTMLInputElement | null>(null);
    const contentFileInputRef = useRef<HTMLInputElement | null>(null);
    const profileImageInputRef = useRef<HTMLInputElement | null>(null);
    const previewRef = useRef<HTMLElement | null>(null);

    const parseJson = async (res: Response) => {
        try {
            return await res.json();
        } catch {
            return { success: false, error: '响应格式错误' };
        }
    };

    const ensureCategoryDefault = (list: CategoryItem[]) => {
        if (!list.length) {
            return 'general';
        }
        return list[0].slug;
    };

    const pickValidCategory = (target: string, list: CategoryItem[]) => {
        if (!list.length) {
            return target || 'general';
        }

        if (target && list.some((item) => item.slug === target)) {
            return target;
        }

        return ensureCategoryDefault(list);
    };

    const autosaveKey = useMemo(() => {
        return editingSlug
            ? `${AUTOSAVE_PREFIX}:edit:${editingSlug}`
            : `${AUTOSAVE_PREFIX}:new`;
    }, [editingSlug]);

    useEffect(() => {
        checkAuth();
    }, []);

    useEffect(() => {
        if (activeTab !== 'editor') {
            return;
        }

        let timer: ReturnType<typeof setTimeout>;
        timer = setTimeout(() => {
            const payload = {
                ...formData,
                savedAt: new Date().toISOString(),
            };
            localStorage.setItem(autosaveKey, JSON.stringify(payload));
            setAutosaveHint('已自动存稿');
        }, 1200);

        return () => clearTimeout(timer);
    }, [activeTab, autosaveKey, formData]);

    useEffect(() => {
        if (editorMode !== 'preview' || !isAuthenticated || activeTab !== 'editor') {
            return;
        }

        let canceled = false;
        const timer = setTimeout(async () => {
            try {
                setPreviewLoading(true);
                const res = await fetch('/api/blog-admin/render-markdown', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ content: formData.content }),
                });

                if (res.status === 401) {
                    setIsAuthenticated(false);
                    setAuthError('登录状态已失效，请重新登录');
                    return;
                }

                const data = await parseJson(res);
                if (!canceled && data.success) {
                    setPreviewHtml(data.data?.html || '');
                }
            } catch (error) {
                console.error('渲染预览失败:', error);
            } finally {
                if (!canceled) {
                    setPreviewLoading(false);
                }
            }
        }, 350);

        return () => {
            canceled = true;
            clearTimeout(timer);
        };
    }, [activeTab, editorMode, formData.content, isAuthenticated]);

    const checkAuth = async () => {
        setAuthChecking(true);
        try {
            const res = await fetch('/api/auth/me', { credentials: 'include' });
            const data = await parseJson(res);

            if (res.ok && data.success) {
                setIsAuthenticated(true);
                setAuthError('');
                await fetchData('');
            } else {
                setIsAuthenticated(false);
                setLoading(false);
            }
        } catch (error) {
            console.error('检查登录状态失败:', error);
            setIsAuthenticated(false);
            setLoading(false);
        } finally {
            setAuthChecking(false);
        }
    };

    const fetchData = async (search: string) => {
        setLoading(true);
        try {
            const [postsRes, categoriesRes, statsRes, trashRes, commentsRes, profileRes] = await Promise.all([
                fetch(`/api/blog-admin/posts?page=1&pageSize=500&published=false&search=${encodeURIComponent(search)}`, {
                    credentials: 'include',
                }),
                fetch('/api/blog-admin/categories', { credentials: 'include' }),
                fetch('/api/blog-admin/stats', { credentials: 'include' }),
                fetch(`/api/blog-admin/trash?search=${encodeURIComponent(trashSearchText)}`, { credentials: 'include' }),
                fetch('/api/blog-admin/comments?status=pending', { credentials: 'include' }),
                fetch('/api/blog-admin/profile', { credentials: 'include' }),
            ]);

            if ([postsRes, categoriesRes, statsRes, trashRes, commentsRes, profileRes].some((item) => item.status === 401)) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const postsData = await parseJson(postsRes);
            const categoriesData = await parseJson(categoriesRes);
            const statsData = await parseJson(statsRes);
            const trashData = await parseJson(trashRes);
            const commentsData = await parseJson(commentsRes);
            const profileData = await parseJson(profileRes);

            const loadedCategories = categoriesData.success ? (categoriesData.data || []) : [];

            if (postsData.success) {
                setPosts(postsData.data || []);
            }

            if (categoriesData.success) {
                setCategories(loadedCategories);
                setBulkCategory((prev) => pickValidCategory(prev, loadedCategories));
                setFormData((prev) => ({
                    ...prev,
                    category: pickValidCategory(prev.category, loadedCategories),
                }));
            }

            if (statsData.success) {
                setStats(statsData.data || null);
            }

            if (trashData.success) {
                setTrashPosts(trashData.data || []);
            }

            if (commentsData.success) {
                setReviewComments(commentsData.data || []);
            }

            if (profileData.success) {
                setProfileForm({
                    name: profileData.data.name || '',
                    title: profileData.data.title || '',
                    bio: profileData.data.bio || '',
                    avatar: profileData.data.avatar || '',
                    location: profileData.data.location || '',
                    email: profileData.data.email || '',
                    website: profileData.data.website || '',
                    social: {
                        github: profileData.data.social?.github || '',
                        linkedin: profileData.data.social?.linkedin || '',
                        twitter: profileData.data.social?.twitter || '',
                        zhihu: profileData.data.social?.zhihu || '',
                    },
                    skills: profileData.data.skills || [],
                });
            }
        } catch (error) {
            console.error('获取后台数据失败:', error);
        } finally {
            setLoading(false);
        }
    };

    const resetCategoryForm = () => {
        const nextOrder = categories.reduce((max, item) => Math.max(max, item.order || 0), 0) + 1;
        setEditingCategorySlug(null);
        setCategoryForm({
            ...EMPTY_CATEGORY_FORM,
            color: '#6b7280',
            order: nextOrder,
        });
    };

    const beginEditCategory = (category: CategoryItem) => {
        setEditingCategorySlug(category.slug);
        setCategoryForm({
            name: category.name,
            slug: category.slug,
            description: category.description || '',
            color: category.color || '#6b7280',
            order: Number.isFinite(category.order) ? Number(category.order) : 1,
        });
    };

    const submitCategory = async () => {
        const name = categoryForm.name.trim();
        if (!name) {
            window.alert('分类名称不能为空');
            return;
        }

        if (!/^#[0-9a-fA-F]{6}$/.test(categoryForm.color)) {
            window.alert('颜色格式必须是 #RRGGBB');
            return;
        }

        setCategorySubmitting(true);
        try {
            const isEdit = Boolean(editingCategorySlug);
            const url = isEdit
                ? `/api/blog-admin/categories/${encodeURIComponent(editingCategorySlug as string)}`
                : '/api/blog-admin/categories';
            const method = isEdit ? 'PUT' : 'POST';

            const body: Record<string, unknown> = {
                name,
                description: categoryForm.description.trim(),
                color: categoryForm.color,
            };

            if (isEdit) {
                body.order = Number(categoryForm.order) || 1;
            } else if (categoryForm.slug.trim()) {
                body.slug = categoryForm.slug.trim();
            }

            const res = await fetch(url, {
                method,
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!res.ok || !data.success) {
                window.alert(data.error || '分类保存失败');
                return;
            }

            window.alert(isEdit ? '分类更新成功' : '分类创建成功');
            await fetchData(searchText);
            resetCategoryForm();
        } catch (error) {
            console.error('保存分类失败:', error);
            window.alert('保存分类失败，请重试');
        } finally {
            setCategorySubmitting(false);
        }
    };

    const removeCategory = async (category: CategoryItem) => {
        if (!window.confirm(`确定删除分类「${category.name}」吗？`)) {
            return;
        }

        try {
            const res = await fetch(`/api/blog-admin/categories/${encodeURIComponent(category.slug)}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!res.ok || !data.success) {
                window.alert(data.error || '删除分类失败');
                return;
            }

            if (editingCategorySlug === category.slug) {
                resetCategoryForm();
            }

            await fetchData(searchText);
        } catch (error) {
            console.error('删除分类失败:', error);
            window.alert('删除分类失败，请重试');
        }
    };

    const fetchTrash = async (search: string) => {
        try {
            const res = await fetch(`/api/blog-admin/trash?search=${encodeURIComponent(search)}`, {
                credentials: 'include',
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (data.success) {
                setTrashPosts(data.data || []);
            }
        } catch (error) {
            console.error('获取回收站失败:', error);
        }
    };

    const fetchReviewComments = async (status: 'pending' | 'all' | 'approved' | 'rejected', search: string) => {
        try {
            const res = await fetch(
                `/api/blog-admin/comments?status=${encodeURIComponent(status)}&search=${encodeURIComponent(search)}`,
                {
                    credentials: 'include',
                },
            );

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (data.success) {
                setReviewComments(data.data || []);
            }
        } catch (error) {
            console.error('获取评论审核数据失败:', error);
        }
    };

    const loadAutosave = (key: string) => {
        const raw = localStorage.getItem(key);
        if (!raw) {
            return null;
        }

        try {
            const parsed = JSON.parse(raw);
            return parsed as EditorForm & { savedAt?: string };
        } catch {
            return null;
        }
    };

    const clearAutosave = (key: string) => {
        localStorage.removeItem(key);
        setAutosaveHint('');
    };

    const insertMarkdownAtCursor = (markdown: string) => {
        const textarea = editorTextareaRef.current;
        if (!textarea) {
            setFormData((prev) => ({
                ...prev,
                content: `${prev.content}${markdown}`,
            }));
            return;
        }

        const start = textarea.selectionStart ?? formData.content.length;
        const end = textarea.selectionEnd ?? formData.content.length;

        setFormData((prev) => ({
            ...prev,
            content: `${prev.content.slice(0, start)}${markdown}${prev.content.slice(end)}`,
        }));

        requestAnimationFrame(() => {
            textarea.focus();
            const nextPos = start + markdown.length;
            textarea.setSelectionRange(nextPos, nextPos);
        });
    };

    const insertCodeBlock = (lang: string) => {
        setCodeLangOpen(false);
        const markdown = `\n\`\`\`${lang}\n// 在此输入代码\n\`\`\`\n`;
        insertMarkdownAtCursor(markdown);
    };

    // 预览代码块复制按钮
    useEffect(() => {
        if (editorMode !== 'preview' || !previewHtml) return;
        const timer = window.setTimeout(() => {
            const article = previewRef.current;
            if (!article) return;
            article.querySelectorAll('pre').forEach((pre) => {
                if (pre.parentElement?.classList.contains('code-block-wrapper')) return;
                const wrapper = document.createElement('div');
                wrapper.className = 'code-block-wrapper relative group';
                const code = pre.querySelector('code');
                const text = code?.textContent || pre.textContent || '';
                const copySvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/><\/svg>`;
                const checkSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/><\/svg>`;
                const btn = document.createElement('button');
                btn.className = 'absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-700/80 hover:bg-slate-600 text-slate-300 hover:text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-all border border-slate-600 hover:border-slate-500';
                btn.innerHTML = `${copySvg}<span>复制</span>`;
                btn.addEventListener('click', async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                        await navigator.clipboard.writeText(text);
                        btn.innerHTML = `${checkSvg}<span>已复制</span>`;
                        btn.classList.add('!opacity-100', '!text-emerald-400', '!border-emerald-500/50');
                        setTimeout(() => {
                            btn.innerHTML = `${copySvg}<span>复制</span>`;
                            btn.classList.remove('!opacity-100', '!text-emerald-400', '!border-emerald-500/50');
                        }, 2000);
                    } catch {
                        btn.innerHTML = `<span>复制失败</span>`;
                        setTimeout(() => {
                            btn.innerHTML = `${copySvg}<span>复制</span>`;
                        }, 2000);
                    }
                });
                pre.parentNode?.insertBefore(wrapper, pre);
                wrapper.appendChild(pre);
                wrapper.appendChild(btn);
            });
        }, 150);
        return () => clearTimeout(timer);
    }, [previewHtml, editorMode]);

    // 点击空白处关闭语言下拉
    useEffect(() => {
        if (!codeLangOpen) return;
        const handler = () => setCodeLangOpen(false);
        window.addEventListener('click', handler);
        return () => window.removeEventListener('click', handler);
    }, [codeLangOpen]);

    const selectImageForInsert = () => {
        if (imageUploading) {
            return;
        }

        imageInputRef.current?.click();
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const file = input.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            window.alert('请选择图片文件');
            input.value = '';
            return;
        }

        if (file.size > 8 * 1024 * 1024) {
            window.alert('图片大小不能超过 8MB');
            input.value = '';
            return;
        }

        setImageUploading(true);
        setUploadHint('图片上传中...');

        try {
            const form = new FormData();
            form.append('image', file);

            const res = await fetch('/api/blog-admin/media/upload', {
                method: 'POST',
                credentials: 'include',
                body: form,
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!res.ok || !data.success) {
                window.alert(data.error || '图片上传失败');
                setUploadHint('');
                return;
            }

            const imagePath = data.data?.absoluteUrl || data.data?.url;
            if (!imagePath) {
                window.alert('图片上传成功但未返回访问地址');
                setUploadHint('');
                return;
            }

            let imageUrl = imagePath;
            if (typeof imagePath === 'string' && imagePath.startsWith('/')) {
                imageUrl = `${window.location.origin}${imagePath}`;
            }

            const alt = file.name
                .replace(/\.[^.]+$/, '')
                .replace(/[\[\]\(\)]/g, '')
                .trim() || 'image';
            insertMarkdownAtCursor(`\n![${alt}](${imageUrl})\n`);
            setEditorMode('markdown');
            setUploadHint('图片已上传并插入正文');
        } catch (error) {
            console.error('上传图片失败:', error);
            window.alert('上传图片失败，请重试');
            setUploadHint('');
        } finally {
            setImageUploading(false);
            input.value = '';
        }
    };

    const selectContentFileForImport = () => {
        if (textUploading) {
            return;
        }

        contentFileInputRef.current?.click();
    };

    const handleContentFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const file = input.files?.[0];
        if (!file) {
            return;
        }

        const isSupported = /\.(txt|md|markdown)$/i.test(file.name || '');
        if (!isSupported) {
            window.alert('仅支持 .txt / .md / .markdown 文件');
            input.value = '';
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            window.alert('文件大小不能超过 2MB');
            input.value = '';
            return;
        }

        if (formData.content.trim()) {
            const shouldReplace = window.confirm('导入文件会替换当前正文内容，是否继续？');
            if (!shouldReplace) {
                input.value = '';
                return;
            }
        }

        setTextUploading(true);
        setUploadHint('文件导入中...');

        try {
            const form = new FormData();
            form.append('file', file);

            const res = await fetch('/api/blog-admin/content/import', {
                method: 'POST',
                credentials: 'include',
                body: form,
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!res.ok || !data.success) {
                window.alert(data.error || '文件导入失败');
                setUploadHint('');
                return;
            }

            const imported = (data.data || {}) as ImportedContentPayload;
            const importedContent = typeof imported.content === 'string' ? imported.content : '';
            if (!importedContent.trim()) {
                window.alert('文件内容为空，无法导入');
                setUploadHint('');
                return;
            }

            setFormData((prev) => ({
                ...prev,
                title: imported.title?.trim() ? imported.title : prev.title,
                excerpt: imported.excerpt?.trim() ? imported.excerpt : prev.excerpt,
                content: importedContent,
                category: imported.category
                    ? pickValidCategory(imported.category, categories)
                    : prev.category,
                tags: Array.isArray(imported.tags) ? imported.tags.join(', ') : prev.tags,
                featured: typeof imported.featured === 'boolean' ? imported.featured : prev.featured,
                published: typeof imported.published === 'boolean' ? imported.published : prev.published,
            }));

            setEditorMode('markdown');
            setUploadHint(imported.format === 'markdown' ? 'Markdown 已导入编辑器' : 'TXT 已导入编辑器');
        } catch (error) {
            console.error('导入文件失败:', error);
            window.alert('导入文件失败，请重试');
            setUploadHint('');
        } finally {
            setTextUploading(false);
            input.value = '';
        }
    };

    const selectProfileImage = () => {
        if (profileImageUploading) {
            return;
        }

        profileImageInputRef.current?.click();
    };

    const handleProfileImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const file = input.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            window.alert('请选择图片文件');
            input.value = '';
            return;
        }

        if (file.size > 8 * 1024 * 1024) {
            window.alert('图片大小不能超过 8MB');
            input.value = '';
            return;
        }

        setProfileImageUploading(true);
        setProfileImageHint('头像上传中...');

        try {
            const form = new FormData();
            form.append('image', file);

            const res = await fetch('/api/blog-admin/media/upload', {
                method: 'POST',
                credentials: 'include',
                body: form,
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!res.ok || !data.success) {
                window.alert(data.error || '头像上传失败');
                setProfileImageHint('');
                return;
            }

            const imagePath = data.data?.absoluteUrl || data.data?.url;
            if (!imagePath) {
                window.alert('头像上传成功但未返回访问地址');
                setProfileImageHint('');
                return;
            }

            let imageUrl = imagePath;
            if (typeof imagePath === 'string' && imagePath.startsWith('/')) {
                imageUrl = `${window.location.origin}${imagePath}`;
            }

            setProfileForm(prev => ({
                ...prev,
                avatar: imageUrl
            }));
            setProfileImageHint('头像已上传');
        } catch (error) {
            console.error('上传头像失败:', error);
            window.alert('上传头像失败，请重试');
            setProfileImageHint('');
        } finally {
            setProfileImageUploading(false);
            input.value = '';
        }
    };

    const saveProfile = async () => {
        setProfileLoading(true);
        try {
            const res = await fetch('/api/blog-admin/profile', {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileForm),
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!data.success) {
                window.alert(data.error || '保存个人资料失败');
                return;
            }

            window.alert('个人资料更新成功');
            setProfileImageHint('');
        } catch (error) {
            console.error('保存个人资料失败:', error);
            window.alert('保存个人资料失败，请重试');
        } finally {
            setProfileLoading(false);
        }
    };

    const handleLogin = async (event: React.FormEvent) => {
        event.preventDefault();
        setAuthError('');
        setLoginLoading(true);

        const payload = {
            username: loginForm.username.trim(),
            password: loginForm.password.trim(),
        };

        if (!payload.username || !payload.password) {
            setAuthError('请输入用户名和密码');
            setLoginLoading(false);
            setAuthChecking(false);
            return;
        }

        try {
            const res = await fetch('/api/auth/login', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await parseJson(res);
            if (!res.ok || !data.success) {
                setAuthError(data.error || '登录失败，请重试');
                return;
            }

            setIsAuthenticated(true);
            setLoginForm({ username: '', password: '' });
            await fetchData(searchText);
        } catch (error) {
            console.error('登录失败:', error);
            setAuthError('登录失败，请检查网络连接后重试');
        } finally {
            setLoginLoading(false);
            setAuthChecking(false);
        }
    };

    const handleLogout = async () => {
        try {
            await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include',
            });
        } catch (error) {
            console.error('退出登录失败:', error);
        } finally {
            setIsAuthenticated(false);
            setPosts([]);
            setStats(null);
            setCategories([]);
            setTrashPosts([]);
            setSelectedSlugs([]);
            setActiveTab('posts');
            setEditingSlug(null);
            setEditingCategorySlug(null);
            setCategoryForm(EMPTY_CATEGORY_FORM);
            setLoading(false);
        }
    };

    const resetEditor = () => {
        const categoryDefault = ensureCategoryDefault(categories);
        setEditingSlug(null);
        setFormData({ ...EMPTY_EDITOR, category: categoryDefault });
        setEditorMode('markdown');
        setPreviewHtml('');
        setUploadHint('');

        const saved = loadAutosave(`${AUTOSAVE_PREFIX}:new`);
        if (saved) {
            setFormData({
                title: saved.title || '',
                excerpt: saved.excerpt || '',
                content: saved.content || '',
                category: saved.category || categoryDefault,
                tags: saved.tags || '',
                featured: Boolean(saved.featured),
                published: Boolean(saved.published),
                pending: Boolean(saved.pending),
                visibility: saved.visibility || 'public',
                coverImage: saved.coverImage || '',
            });
            setAutosaveHint(`已恢复存稿 ${saved.savedAt ? new Date(saved.savedAt).toLocaleString('zh-CN') : ''}`.trim());
        }
    };

    const startEditing = async (post: Post) => {
        try {
            const res = await fetch(`/api/blog-admin/posts/${post.slug}?track=false`, {
                credentials: 'include',
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!data.success) {
                window.alert(data.error || '加载文章详情失败');
                return;
            }

            const fullPost = data.data as Post;
            const saved = loadAutosave(`${AUTOSAVE_PREFIX}:edit:${post.slug}`);

            setEditingSlug(post.slug);
            setFormData({
                title: saved?.title ?? fullPost.title,
                excerpt: saved?.excerpt ?? (fullPost.excerpt || ''),
                content: saved?.content ?? (fullPost.content || ''),
                category: saved?.category ?? fullPost.category,
                tags: saved?.tags ?? ((fullPost.tags || []).join(', ')),
                featured: saved?.featured ?? Boolean(fullPost.featured),
                published: saved?.published ?? Boolean(fullPost.published),
                pending: saved?.pending ?? Boolean(fullPost.pending),
                visibility: saved?.visibility ?? (fullPost.visibility || 'public'),
                coverImage: saved?.coverImage ?? ((fullPost as any).coverImage || ''),
            });

            setAutosaveHint(saved ? '已恢复该文章的本地存稿' : '');
            setUploadHint('');
            setActiveTab('editor');
            setEditorMode('markdown');
        } catch (error) {
            console.error('加载文章详情失败:', error);
            window.alert('加载文章详情失败，请重试');
        }
    };

    const submitPost = async () => {
        if (!formData.title.trim() || !formData.content.trim()) {
            window.alert('标题和内容不能为空');
            return;
        }

        const payload = {
            title: formData.title.trim(),
            excerpt: formData.excerpt.trim(),
            content: formData.content,
            category: formData.category,
            tags: formData.tags.split(',').map((item) => item.trim()).filter(Boolean),
            featured: formData.featured,
            published: formData.published,
            pending: formData.pending,
            visibility: formData.visibility,
            coverImage: formData.coverImage.trim() || undefined,
        };

        try {
            const url = editingSlug ? `/api/blog-admin/posts/${editingSlug}` : '/api/blog-admin/posts';
            const method = editingSlug ? 'PUT' : 'POST';
            const res = await fetch(url, {
                method,
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!data.success) {
                window.alert(data.error || '保存失败，请重试');
                return;
            }

            clearAutosave(autosaveKey);
            window.alert(editingSlug ? '文章更新成功' : (formData.published ? '文章发布成功' : formData.pending ? '已保存到待发布列表' : '草稿保存成功'));
            resetEditor();
            setActiveTab('posts');
            await fetchData(searchText);
        } catch (error) {
            console.error('保存文章失败:', error);
            window.alert('保存文章失败，请重试');
        }
    };

    const moveToTrash = async (slug: string) => {
        if (!window.confirm('确定将这篇文章移入回收站吗？')) {
            return;
        }

        try {
            const res = await fetch(`/api/blog-admin/posts/${slug}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!data.success) {
                window.alert(data.error || '删除失败');
                return;
            }

            setSelectedSlugs((prev) => prev.filter((item) => item !== slug));
            await fetchData(searchText);
        } catch (error) {
            console.error('删除文章失败:', error);
            window.alert('删除失败，请重试');
        }
    };

    const handleBulkAction = async (action: 'moveToTrash' | 'setPublished' | 'setDraft' | 'setPending' | 'setCategory') => {
        if (!selectedSlugs.length) {
            window.alert('请先选择文章');
            return;
        }

        const body: Record<string, unknown> = {
            slugs: selectedSlugs,
        };

        if (action === 'moveToTrash') {
            if (!window.confirm(`确定将选中的 ${selectedSlugs.length} 篇文章移入回收站吗？`)) {
                return;
            }
            body.action = 'moveToTrash';
        }

        if (action === 'setPublished') {
            body.action = 'setPublished';
            body.published = true;
        }

        if (action === 'setDraft') {
            body.action = 'setPublished';
            body.published = false;
        }

        if (action === 'setPending') {
            body.action = 'setPending';
        }

        if (action === 'setCategory') {
            if (!bulkCategory) {
                window.alert('请选择目标分类');
                return;
            }
            body.action = 'setCategory';
            body.category = bulkCategory;
        }

        try {
            const res = await fetch('/api/blog-admin/posts/bulk', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!data.success) {
                window.alert(data.error || '批量操作失败');
                return;
            }

            setSelectedSlugs([]);
            await fetchData(searchText);
        } catch (error) {
            console.error('批量操作失败:', error);
            window.alert('批量操作失败，请重试');
        }
    };

    const restoreTrashPost = async (slug: string) => {
        try {
            const res = await fetch(`/api/blog-admin/trash/${slug}/restore`, {
                method: 'POST',
                credentials: 'include',
            });

            const data = await parseJson(res);
            if (!data.success) {
                window.alert(data.error || '恢复失败');
                return;
            }

            await fetchData(searchText);
        } catch (error) {
            console.error('恢复文章失败:', error);
            window.alert('恢复失败，请重试');
        }
    };

    const deleteTrashPost = async (slug: string) => {
        if (!window.confirm('彻底删除后无法恢复，确定继续吗？')) {
            return;
        }

        try {
            const res = await fetch(`/api/blog-admin/trash/${slug}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            const data = await parseJson(res);
            if (!data.success) {
                window.alert(data.error || '彻底删除失败');
                return;
            }

            await fetchData(searchText);
        } catch (error) {
            console.error('彻底删除失败:', error);
            window.alert('彻底删除失败，请重试');
        }
    };

    const moderateComment = async (commentId: string, status: 'approved' | 'rejected') => {
        try {
            const res = await fetch(`/api/blog-admin/comments/${commentId}/moderate`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status }),
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!data.success) {
                window.alert(data.error || '审核失败');
                return;
            }

            await fetchReviewComments(reviewStatus, reviewSearchText);
        } catch (error) {
            console.error('审核评论失败:', error);
            window.alert('审核评论失败，请重试');
        }
    };

    const replyComment = async (commentId: string) => {
        const content = (replyDrafts[commentId] || '').trim();
        if (!content) {
            window.alert('回复内容不能为空');
            return;
        }

        try {
            const res = await fetch(`/api/blog-admin/comments/${commentId}/reply`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ content }),
            });

            if (res.status === 401) {
                setIsAuthenticated(false);
                setAuthError('登录状态已失效，请重新登录');
                return;
            }

            const data = await parseJson(res);
            if (!data.success) {
                window.alert(data.error || '回复失败');
                return;
            }

            setReplyDrafts((prev) => ({ ...prev, [commentId]: '' }));
            await fetchReviewComments(reviewStatus, reviewSearchText);
        } catch (error) {
            console.error('回复评论失败:', error);
            window.alert('回复失败，请重试');
        }
    };

    const filteredPosts = useMemo(() => {
        if (statusFilter === 'all') {
            return posts;
        }

        if (statusFilter === 'published') {
            return posts.filter((post) => Boolean(post.published));
        }

        if (statusFilter === 'pending') {
            return posts.filter((post) => !post.published && post.pending);
        }

        // draft: not published and not pending
        return posts.filter((post) => !post.published && !post.pending);
    }, [posts, statusFilter]);

    const allSelected = filteredPosts.length > 0 && filteredPosts.every((post) => selectedSlugs.includes(post.slug));

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedSlugs((prev) => prev.filter((slug) => !filteredPosts.some((post) => post.slug === slug)));
            return;
        }

        const merged = new Set(selectedSlugs);
        filteredPosts.forEach((post) => merged.add(post.slug));
        setSelectedSlugs(Array.from(merged));
    };

    const toggleSelectOne = (slug: string) => {
        setSelectedSlugs((prev) => {
            if (prev.includes(slug)) {
                return prev.filter((item) => item !== slug);
            }
            return [...prev, slug];
        });
    };

    if (authChecking) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg">验证登录状态中...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center px-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <ShieldCheck className="w-8 h-8 text-blue-600" />
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">后台登录</h1>
                            <p className="text-sm text-gray-500 dark:text-gray-400">请使用管理员账号访问管理功能</p>
                        </div>
                    </div>

                    {authError && (
                        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            {authError}
                        </div>
                    )}

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">用户名</label>
                            <input
                                type="text"
                                required
                                value={loginForm.username}
                                onChange={(event) => setLoginForm((prev) => ({ ...prev, username: event.target.value }))}
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="admin"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">密码</label>
                            <input
                                type="password"
                                required
                                value={loginForm.password}
                                onChange={(event) => setLoginForm((prev) => ({ ...prev, password: event.target.value }))}
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="请输入密码"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loginLoading}
                            className="w-full px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
                        >
                            {loginLoading ? '登录中...' : '登录'}
                        </button>
                    </form>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-4 px-3 sm:py-8 sm:px-4 pb-32 sm:pb-8">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-5 sm:mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                >
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white">博客管理</h1>
                        <p className="text-gray-600 dark:text-gray-400 hidden sm:block text-sm mt-1">草稿、发布、批量编辑、回收站与统计</p>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 flex items-center gap-2 text-sm self-end"
                    >
                        <LogOut className="w-4 h-4" />
                        <span className="hidden sm:inline">退出登录</span>
                    </button>
                </motion.div>

                {/* 移动端横向滚动标签栏 */}
                <div className="mb-4 sm:mb-6 -mx-3 px-3 sm:mx-0 sm:px-0 relative">
                    {/* 右侧渐变遮罩提示可滚动 */}
                    <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-100 to-transparent dark:from-gray-900 pointer-events-none z-10 sm:hidden" />
                    <div className="flex gap-2 sm:gap-3 min-w-max overflow-x-auto scrollbar-hide pb-1">
                    <button
                        onClick={() => setActiveTab('posts')}
                        className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap ${activeTab === 'posts' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800'}`}
                    >
                        文章管理
                    </button>
                    <button
                        onClick={() => {
                            if (activeTab !== 'editor') {
                                resetEditor();
                            }
                            setActiveTab('editor');
                        }}
                        className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap ${activeTab === 'editor' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800'}`}
                    >
                        {editingSlug ? '编辑' : '新建/草稿'}
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('trash');
                            fetchTrash(trashSearchText);
                        }}
                        className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap ${activeTab === 'trash' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800'}`}
                    >
                        回收站
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('comments');
                            fetchReviewComments(reviewStatus, reviewSearchText);
                        }}
                        className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap ${activeTab === 'comments' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800'}`}
                    >
                        评论
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('categories');
                            resetCategoryForm();
                        }}
                        className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap ${activeTab === 'categories' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800'}`}
                    >
                        分类
                    </button>
                    <button
                        onClick={() => setActiveTab('stats')}
                        className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap ${activeTab === 'stats' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800'}`}
                    >
                        统计
                    </button>
                    <button
                        onClick={() => setActiveTab('profile')}
                        className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap ${activeTab === 'profile' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800'}`}
                    >
                        资料
                    </button>
                    </div>
                </div>

                {activeTab === 'posts' && (
                    <div className="space-y-3 sm:space-y-4">
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
                            {/* 移动端紧凑布局：搜索框和按钮一行 */}
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        value={searchText}
                                        onChange={(event) => setSearchText(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                fetchData(searchText);
                                            }
                                        }}
                                        placeholder="搜索..."
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                                    />
                                </div>
                                <select
                                    value={statusFilter}
                                    onChange={(event) => setStatusFilter(event.target.value as 'all' | 'published' | 'pending' | 'draft')}
                                    className="px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs sm:text-sm whitespace-nowrap"
                                >
                                    <option value="all">全部</option>
                                    <option value="published">已发布</option>
                                    <option value="pending">待发布</option>
                                    <option value="draft">草稿</option>
                                </select>
                                <button
                                    onClick={() => fetchData(searchText)}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                                >
                                    搜索
                                </button>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-sm">
                            {/* 移动端改为两行布局 */}
                            <div className="flex flex-wrap items-center gap-2">
                                <button onClick={() => handleBulkAction('setPublished')} className="px-3 py-2 rounded-lg bg-green-600 text-white text-xs sm:text-sm">
                                    发布
                                </button>
                                <button onClick={() => handleBulkAction('setPending')} className="px-3 py-2 rounded-lg bg-blue-600 text-white text-xs sm:text-sm">
                                    待发布
                                </button>
                                <button onClick={() => handleBulkAction('setDraft')} className="px-3 py-2 rounded-lg bg-yellow-600 text-white text-xs sm:text-sm">
                                    草稿
                                </button>
                                <button onClick={() => handleBulkAction('moveToTrash')} className="px-3 py-2 rounded-lg bg-red-600 text-white text-xs sm:text-sm">
                                    删除
                                </button>
                                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 ml-auto">已选 {selectedSlugs.length} 篇</span>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-x-auto">
                            <table className="w-full min-w-[860px]">
                                <thead className="bg-gray-50 dark:bg-gray-900">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs">
                                            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs">标题</th>
                                        <th className="px-4 py-3 text-left text-xs">分类</th>
                                        <th className="px-4 py-3 text-left text-xs">状态</th>
                                        <th className="px-4 py-3 text-left text-xs">访问量</th>
                                        <th className="px-4 py-3 text-left text-xs">更新时间</th>
                                        <th className="px-4 py-3 text-right text-xs">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPosts.map((post) => (
                                        <tr key={post.slug} className="border-t border-gray-100 dark:border-gray-700">
                                            <td className="px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSlugs.includes(post.slug)}
                                                    onChange={() => toggleSelectOne(post.slug)}
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-gray-900 dark:text-gray-100">{post.title}</div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{post.excerpt}</div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{post.categoryName || post.category}</td>
                                            <td className="px-4 py-3 text-sm">
                                                {post.published ? (
                                                    <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">已发布</span>
                                                ) : post.pending ? (
                                                    <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">待发布</span>
                                                ) : (
                                                    <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">草稿</span>
                                                )}
                                                {post.visibility === 'private' && (
                                                    <span className="ml-1 px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">私有</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{post.views || 0}</td>
                                            <td className="px-4 py-3 text-sm">{new Date(post.updatedAt || post.date).toLocaleString('zh-CN')}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex justify-end gap-2">
                                                    <button className="p-2 text-blue-600" onClick={() => startEditing(post)} title="编辑">
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button className="p-2 text-emerald-600" onClick={() => window.open(`/blog/${post.slug}`, '_blank')} title="预览">
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button className="p-2 text-red-600" onClick={() => moveToTrash(post.slug)} title="移入回收站">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {!loading && filteredPosts.length === 0 && (
                                <div className="p-10 text-center text-gray-500 dark:text-gray-400">没有匹配文章</div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'editor' && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 space-y-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-blue-600" />
                                <h2 className="text-xl font-bold text-gray-800 dark:text-white">{editingSlug ? '编辑文章' : '新建文章'}</h2>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                <Clock className="w-4 h-4" />
                                {autosaveHint || '自动存稿已启用'}
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm mb-1">文章标题</label>
                                <input
                                    value={formData.title}
                                    onChange={(event) => setFormData((prev) => ({ ...prev, title: event.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                />
                            </div>
                            <div>
                                <label className="block text-sm mb-1">分类</label>
                                <select
                                    value={formData.category}
                                    onChange={(event) => setFormData((prev) => ({ ...prev, category: event.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                >
                                    {categories.map((category) => (
                                        <option key={category.slug} value={category.slug}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm mb-1">文章摘要</label>
                            <textarea
                                rows={3}
                                value={formData.excerpt}
                                onChange={(event) => setFormData((prev) => ({ ...prev, excerpt: event.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                            />
                        </div>

                        <div>
                            <label className="block text-sm mb-1">文章封面URL</label>
                            <input
                                value={formData.coverImage || ''}
                                onChange={(event) => setFormData((prev) => ({ ...prev, coverImage: event.target.value }))}
                                placeholder="输入图片URL或通过编辑器上传后复制相对路径"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                            />
                        </div>

                        <div>
                            <label className="block text-sm mb-1">标签（逗号分隔）</label>
                            <input
                                value={formData.tags}
                                onChange={(event) => setFormData((prev) => ({ ...prev, tags: event.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                            />
                        </div>

                        <div className="flex items-center gap-5 text-sm">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.featured}
                                    onChange={(event) => setFormData((prev) => ({ ...prev, featured: event.target.checked }))}
                                />
                                精选文章
                            </label>
                            <div className="flex items-center gap-2">
                                <span>发布状态：</span>
                                <select
                                    value={formData.published ? 'published' : (formData.pending ? 'pending' : 'draft')}
                                    onChange={(event) => {
                                        const val = event.target.value;
                                        setFormData((prev) => ({
                                            ...prev,
                                            published: val === 'published',
                                            pending: val === 'pending'
                                        }));
                                    }}
                                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                                >
                                    <option value="published">立即发布</option>
                                    <option value="pending">保存到待发布</option>
                                    <option value="draft">保存为草稿</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <span>可见性：</span>
                                <select
                                    value={formData.visibility}
                                    onChange={(event) => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            visibility: event.target.value as 'public' | 'private'
                                        }));
                                    }}
                                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                                >
                                    <option value="public">公开</option>
                                    <option value="private">私有（登录后可见）</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <input
                                ref={imageInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                className="hidden"
                                onChange={handleImageUpload}
                            />
                            <input
                                ref={contentFileInputRef}
                                type="file"
                                accept=".txt,.md,.markdown,text/plain,text/markdown"
                                className="hidden"
                                onChange={handleContentFileUpload}
                            />
                            <button
                                onClick={() => setEditorMode('markdown')}
                                className={`px-3 py-2 rounded-lg text-sm ${editorMode === 'markdown' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}
                            >
                                Markdown
                            </button>
                            <button
                                onClick={() => setEditorMode('preview')}
                                className={`px-3 py-2 rounded-lg text-sm ${editorMode === 'preview' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}
                            >
                                预览
                            </button>
                            <button
                                onClick={selectImageForInsert}
                                disabled={imageUploading}
                                className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1 ${imageUploading ? 'bg-gray-300 text-gray-600 cursor-not-allowed dark:bg-gray-600 dark:text-gray-300' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                            >
                                <Upload className="w-4 h-4" />
                                {imageUploading ? '上传中...' : '上传并插图'}
                            </button>
                            <button
                                onClick={selectContentFileForImport}
                                disabled={textUploading}
                                className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1 ${textUploading ? 'bg-gray-300 text-gray-600 cursor-not-allowed dark:bg-gray-600 dark:text-gray-300' : 'bg-sky-600 text-white hover:bg-sky-700'}`}
                            >
                                <FileText className="w-4 h-4" />
                                {textUploading ? '导入中...' : '导入 TXT/MD'}
                            </button>
                            <button
                                onClick={() => clearAutosave(autosaveKey)}
                                className="px-3 py-2 rounded-lg text-sm bg-gray-100 dark:bg-gray-700"
                            >
                                清理本地存稿
                            </button>
                        </div>

                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            图片支持 JPG / PNG / WebP / GIF（单张不超过 8MB）；文本导入支持 TXT / Markdown（单文件不超过 2MB）。
                            {uploadHint ? <span className="text-emerald-600 dark:text-emerald-400 ml-2">{uploadHint}</span> : null}
                        </div>

                        {editorMode === 'markdown' && (
                            <div>
                                {/* Markdown Toolbar */}
                                <div className="mb-2 flex flex-wrap items-center gap-1 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('**粗体文字**')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="粗体 (Ctrl+B)"
                                    >
                                        <Bold className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('*斜体文字*')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="斜体 (Ctrl+I)"
                                    >
                                        <Italic className="w-4 h-4" />
                                    </button>
                                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('\n# 一级标题\n')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="一级标题"
                                    >
                                        <Heading1 className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('\n## 二级标题\n')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="二级标题"
                                    >
                                        <Heading2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('\n### 三级标题\n')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="三级标题"
                                    >
                                        <Heading3 className="w-4 h-4" />
                                    </button>
                                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('\n> 引用文字\n')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="引用"
                                    >
                                        <Quote className="w-4 h-4" />
                                    </button>
                                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setCodeLangOpen(!codeLangOpen); }}
                                            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-1"
                                            title="代码块"
                                        >
                                            <Code className="w-4 h-4" />
                                            <ChevronDown className="w-3 h-3" />
                                        </button>
                                        {codeLangOpen && (
                                            <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-1 w-40 max-h-64 overflow-y-auto">
                                                <button
                                                    type="button"
                                                    onClick={() => insertCodeBlock('')}
                                                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                                >
                                                    无语言
                                                </button>
                                                {['javascript','typescript','python','bash','json','html','css','sql','java','cpp','go','rust','yaml','markdown'].map((lang) => (
                                                    <button
                                                        key={lang}
                                                        type="button"
                                                        onClick={() => insertCodeBlock(lang)}
                                                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-mono"
                                                    >
                                                        {lang}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('`行内代码`')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="行内代码"
                                    >
                                        <span className="w-4 h-4 font-mono text-xs">{"</>"}</span>
                                    </button>
                                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('\n- 无序列表项\n')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="无序列表"
                                    >
                                        <List className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('\n1. 有序列表项\n')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="有序列表"
                                    >
                                        <ListOrdered className="w-4 h-4" />
                                    </button>
                                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('\n[链接文字](https://example.com)\n')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="链接"
                                    >
                                        <Link className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={selectImageForInsert}
                                        disabled={imageUploading}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                                        title="插入图片"
                                    >
                                        <Image className="w-4 h-4" />
                                    </button>
                                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('\n| 表头1 | 表头2 |\n| ------ | ------ |\n| 单元格 | 单元格 |\n')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="表格"
                                    >
                                        <Table className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => insertMarkdownAtCursor('\n---\n')}
                                        className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        title="分隔线"
                                    >
                                        <Minus className="w-4 h-4" />
                                    </button>
                                </div>
                                {/* Editor Textarea */}
                                <textarea
                                    ref={editorTextareaRef}
                                    rows={60}
                                    value={formData.content}
                                    onChange={(event) => setFormData((prev) => ({ ...prev, content: event.target.value }))}
                                    className="w-full min-h-[560px] px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 font-mono text-sm"
                                    placeholder="# 标题&#10;&#10;开始写作..."
                                />
                            </div>
                        )}

                        {editorMode === 'preview' && (
                            <div className="min-h-[560px] border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                {previewLoading ? (
                                    <div className="text-sm text-gray-500">预览生成中...</div>
                                ) : (
                                    <article
                                        ref={previewRef}
                                        className="prose prose-sm md:prose-base dark:prose-invert max-w-none"
                                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                                    />
                                )}
                            </div>
                        )}

                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={submitPost}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                            >
                                <Save className="w-4 h-4" />
                                {formData.published ? '保存并发布' : formData.pending ? '保存到待发布' : '保存草稿'}
                            </button>
                            <button
                                onClick={resetEditor}
                                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                新建空白
                            </button>
                            <button
                                onClick={() => {
                                    resetEditor();
                                    setActiveTab('posts');
                                }}
                                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center gap-2"
                            >
                                <X className="w-4 h-4" />
                                返回列表
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'trash' && (
                    <div className="space-y-4">
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm flex flex-wrap items-center gap-3">
                            <div className="relative flex-1 min-w-[240px]">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    value={trashSearchText}
                                    onChange={(event) => setTrashSearchText(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            fetchTrash(trashSearchText);
                                        }
                                    }}
                                    placeholder="搜索回收站"
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                />
                            </div>
                            <button
                                onClick={() => fetchTrash(trashSearchText)}
                                className="px-3 py-2 bg-blue-600 text-white rounded-lg"
                            >
                                搜索
                            </button>
                            <button
                                onClick={() => fetchTrash(trashSearchText)}
                                className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center gap-1"
                            >
                                <RefreshCw className="w-4 h-4" />
                                刷新
                            </button>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-x-auto">
                            <table className="w-full min-w-[760px]">
                                <thead className="bg-gray-50 dark:bg-gray-900">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs">标题</th>
                                        <th className="px-4 py-3 text-left text-xs">原始Slug</th>
                                        <th className="px-4 py-3 text-left text-xs">删除时间</th>
                                        <th className="px-4 py-3 text-right text-xs">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {trashPosts.map((post) => (
                                        <tr key={post.slug} className="border-t border-gray-100 dark:border-gray-700">
                                            <td className="px-4 py-3">{post.title}</td>
                                            <td className="px-4 py-3 text-sm text-gray-500">{post.originalSlug}</td>
                                            <td className="px-4 py-3 text-sm text-gray-500">{new Date(post.deletedAt).toLocaleString('zh-CN')}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex justify-end gap-2">
                                                    <button className="px-3 py-1 text-sm bg-green-600 text-white rounded" onClick={() => restoreTrashPost(post.slug)}>
                                                        <Upload className="w-4 h-4 inline mr-1" />
                                                        恢复
                                                    </button>
                                                    <button className="px-3 py-1 text-sm bg-red-600 text-white rounded" onClick={() => deleteTrashPost(post.slug)}>
                                                        <Trash2 className="w-4 h-4 inline mr-1" />
                                                        彻底删除
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {!loading && trashPosts.length === 0 && (
                                <div className="p-10 text-center text-gray-500 dark:text-gray-400">回收站为空</div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'comments' && (
                    <div className="space-y-4">
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
                            <div className="grid md:grid-cols-4 gap-3">
                                <div className="md:col-span-2 relative">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        value={reviewSearchText}
                                        onChange={(event) => setReviewSearchText(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                fetchReviewComments(reviewStatus, reviewSearchText);
                                            }
                                        }}
                                        placeholder="搜索评论内容/作者/文章"
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                    />
                                </div>

                                <select
                                    value={reviewStatus}
                                    onChange={(event) => setReviewStatus(event.target.value as 'pending' | 'all' | 'approved' | 'rejected')}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                >
                                    <option value="pending">待审核</option>
                                    <option value="approved">已通过</option>
                                    <option value="rejected">已拒绝</option>
                                    <option value="all">全部</option>
                                </select>

                                <button
                                    onClick={() => fetchReviewComments(reviewStatus, reviewSearchText)}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    查询
                                </button>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {reviewComments.map((comment) => (
                                <div key={comment.id} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
                                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                                        <div className="flex items-center gap-2">
                                            <MessageSquare className="w-4 h-4 text-blue-500" />
                                            <span className="font-medium text-gray-800 dark:text-gray-100">{comment.author}</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {new Date(comment.createdAt).toLocaleString('zh-CN')}
                                            </span>
                                        </div>
                                        <span className={`px-2 py-1 rounded-full text-xs ${
                                            comment.status === 'approved'
                                                ? 'bg-green-100 text-green-700'
                                                : comment.status === 'rejected'
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-yellow-100 text-yellow-700'
                                        }`}
                                        >
                                            {comment.status === 'approved' ? '已通过' : comment.status === 'rejected' ? '已拒绝' : '待审核'}
                                        </span>
                                    </div>

                                    <div className="text-sm text-gray-600 dark:text-gray-300 mb-3 whitespace-pre-wrap">{comment.content}</div>

                                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                        文章：
                                        <a href={`/blog/${comment.postSlug}`} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline ml-1">
                                            /blog/{comment.postSlug}
                                        </a>
                                    </div>

                                    <div className="flex flex-wrap gap-2 mb-3">
                                        <button
                                            onClick={() => moderateComment(comment.id, 'approved')}
                                            className="px-3 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 flex items-center gap-1"
                                        >
                                            <Check className="w-4 h-4" />
                                            通过
                                        </button>
                                        <button
                                            onClick={() => moderateComment(comment.id, 'rejected')}
                                            className="px-3 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center gap-1"
                                        >
                                            <XCircle className="w-4 h-4" />
                                            拒绝
                                        </button>
                                    </div>

                                    <div className="flex gap-2">
                                        <input
                                            value={replyDrafts[comment.id] || ''}
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                setReplyDrafts((prev) => ({ ...prev, [comment.id]: value }));
                                            }}
                                            placeholder="管理员回复..."
                                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                                        />
                                        <button
                                            onClick={() => replyComment(comment.id)}
                                            className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1"
                                        >
                                            <Send className="w-4 h-4" />
                                            回复
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {!loading && reviewComments.length === 0 && (
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-10 text-center text-gray-500 dark:text-gray-400">
                                    暂无符合条件的评论
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'categories' && (
                    <div className="space-y-4">
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm space-y-4">
                            <div className="flex items-center justify-between gap-3">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    {editingCategorySlug ? `编辑分类：${editingCategorySlug}` : '新增分类'}
                                </h2>
                                <button
                                    onClick={resetCategoryForm}
                                    className="px-3 py-2 rounded-lg text-sm bg-gray-100 dark:bg-gray-700"
                                >
                                    重置表单
                                </button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm mb-1">分类名称</label>
                                    <input
                                        value={categoryForm.name}
                                        onChange={(event) => setCategoryForm((prev) => ({ ...prev, name: event.target.value }))}
                                        placeholder="例如：Cloud Native"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm mb-1">分类 Slug</label>
                                    <input
                                        value={categoryForm.slug}
                                        disabled={Boolean(editingCategorySlug)}
                                        onChange={(event) => setCategoryForm((prev) => ({ ...prev, slug: event.target.value }))}
                                        placeholder="例如：cloud-native（留空将自动生成）"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 disabled:opacity-60"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm mb-1">颜色（Hex）</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={categoryForm.color}
                                            onChange={(event) => setCategoryForm((prev) => ({ ...prev, color: event.target.value }))}
                                            className="w-12 h-10 p-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                                        />
                                        <input
                                            value={categoryForm.color}
                                            onChange={(event) => setCategoryForm((prev) => ({ ...prev, color: event.target.value }))}
                                            placeholder="#6b7280"
                                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm mb-1">排序（数字越小越靠前）</label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={categoryForm.order}
                                        onChange={(event) => {
                                            const value = Number(event.target.value) || 1;
                                            setCategoryForm((prev) => ({ ...prev, order: value }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm mb-1">描述</label>
                                <textarea
                                    rows={3}
                                    value={categoryForm.description}
                                    onChange={(event) => setCategoryForm((prev) => ({ ...prev, description: event.target.value }))}
                                    placeholder="这个分类主要用于什么内容"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                />
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    onClick={submitCategory}
                                    disabled={categorySubmitting}
                                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                >
                                    {categorySubmitting ? '保存中...' : (editingCategorySlug ? '保存修改' : '创建分类')}
                                </button>
                                {editingCategorySlug && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        编辑模式下 Slug 不可修改，避免影响已有关联文章。
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-x-auto">
                            <table className="w-full min-w-[860px]">
                                <thead className="bg-gray-50 dark:bg-gray-900">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs">分类</th>
                                        <th className="px-4 py-3 text-left text-xs">Slug</th>
                                        <th className="px-4 py-3 text-left text-xs">描述</th>
                                        <th className="px-4 py-3 text-left text-xs">文章数</th>
                                        <th className="px-4 py-3 text-left text-xs">访问量</th>
                                        <th className="px-4 py-3 text-left text-xs">排序</th>
                                        <th className="px-4 py-3 text-right text-xs">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...categories]
                                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                                        .map((category) => {
                                            const usedCount = category.count || 0;
                                            const disableDelete = usedCount > 0;

                                            return (
                                                <tr key={category.slug} className="border-t border-gray-100 dark:border-gray-700">
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="inline-block w-3 h-3 rounded-full"
                                                                style={{ backgroundColor: category.color || '#6b7280' }}
                                                            />
                                                            <span className="font-medium text-gray-900 dark:text-gray-100">{category.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{category.slug}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{category.description || '-'}</td>
                                                    <td className="px-4 py-3 text-sm">{usedCount}</td>
                                                    <td className="px-4 py-3 text-sm">{category.views || 0}</td>
                                                    <td className="px-4 py-3 text-sm">{category.order || 0}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex justify-end gap-2">
                                                            <button
                                                                onClick={() => beginEditCategory(category)}
                                                                className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                                                            >
                                                                编辑
                                                            </button>
                                                            <button
                                                                onClick={() => removeCategory(category)}
                                                                disabled={disableDelete}
                                                                className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                                                title={disableDelete ? '该分类下仍有文章，无法删除' : '删除分类'}
                                                            >
                                                                删除
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>

                            {!loading && categories.length === 0 && (
                                <div className="p-10 text-center text-gray-500 dark:text-gray-400">暂无分类数据</div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'profile' && (
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-800 dark:text-white">个人资料配置</h2>
                                <button
                                    onClick={saveProfile}
                                    disabled={profileLoading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
                                >
                                    <Save className="w-4 h-4" />
                                    {profileLoading ? '保存中...' : '保存个人资料'}
                                </button>
                            </div>

                            {/* 头像上传 */}
                            <div className="mb-8">
                                <label className="block text-sm font-medium mb-3">头像</label>
                                <div className="flex flex-col md:flex-row items-start gap-6">
                                    <div className="flex-shrink-0">
                                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white dark:border-gray-800 shadow-lg">
                                            {profileForm.avatar ? (
                                                <img 
                                                    src={profileForm.avatar} 
                                                    alt="头像"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                                    <User className="w-16 h-16 text-white" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-1 space-y-3">
                                        <input
                                            ref={profileImageInputRef}
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp,image/gif"
                                            className="hidden"
                                            onChange={handleProfileImageUpload}
                                        />
                                        <button
                                            onClick={selectProfileImage}
                                            disabled={profileImageUploading}
                                            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${profileImageUploading ? 'bg-gray-300 text-gray-600 cursor-not-allowed dark:bg-gray-600 dark:text-gray-300' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                                        >
                                            <Upload className="w-4 h-4" />
                                            {profileImageUploading ? '上传中...' : '上传新头像'}
                                        </button>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            支持 JPG / PNG / WebP / GIF，单张不超过 8MB。
                                            {profileImageHint && <span className="text-emerald-600 dark:text-emerald-400 ml-2">{profileImageHint}</span>}
                                        </div>
                                        <div>
                                            <label className="block text-sm mb-1">头像URL</label>
                                            <input
                                                type="text"
                                                value={profileForm.avatar}
                                                onChange={(e) => setProfileForm(prev => ({ ...prev, avatar: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                                placeholder="https://example.com/avatar.jpg"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 基本信息 */}
                            <div className="grid md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm mb-1">姓名</label>
                                    <input
                                        type="text"
                                        value={profileForm.name}
                                        onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm mb-1">职位/头衔</label>
                                    <input
                                        type="text"
                                        value={profileForm.title}
                                        onChange={(e) => setProfileForm(prev => ({ ...prev, title: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm mb-1">所在地</label>
                                    <input
                                        type="text"
                                        value={profileForm.location}
                                        onChange={(e) => setProfileForm(prev => ({ ...prev, location: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm mb-1">邮箱</label>
                                    <input
                                        type="email"
                                        value={profileForm.email}
                                        onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                    />
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm mb-1">个人简介</label>
                                <textarea
                                    rows={4}
                                    value={profileForm.bio}
                                    onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                />
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm mb-1">个人网站</label>
                                <input
                                    type="url"
                                    value={profileForm.website}
                                    onChange={(e) => setProfileForm(prev => ({ ...prev, website: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                />
                            </div>

                            {/* 社交媒体 */}
                            <div className="mb-8">
                                <h3 className="text-lg font-medium mb-4">社交媒体</h3>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm mb-1">GitHub</label>
                                        <input
                                            type="url"
                                            value={profileForm.social.github}
                                            onChange={(e) => setProfileForm(prev => ({ 
                                                ...prev, 
                                                social: { ...prev.social, github: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm mb-1">LinkedIn</label>
                                        <input
                                            type="url"
                                            value={profileForm.social.linkedin}
                                            onChange={(e) => setProfileForm(prev => ({ 
                                                ...prev, 
                                                social: { ...prev.social, linkedin: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm mb-1">Twitter</label>
                                        <input
                                            type="url"
                                            value={profileForm.social.twitter}
                                            onChange={(e) => setProfileForm(prev => ({ 
                                                ...prev, 
                                                social: { ...prev.social, twitter: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm mb-1">知乎</label>
                                        <input
                                            type="url"
                                            value={profileForm.social.zhihu}
                                            onChange={(e) => setProfileForm(prev => ({ 
                                                ...prev, 
                                                social: { ...prev.social, zhihu: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 技能 */}
                            <div>
                                <h3 className="text-lg font-medium mb-4">技能</h3>
                                <div className="space-y-3">
                                    {profileForm.skills.map((skill, index) => (
                                        <div key={index} className="flex items-center gap-3">
                                            <input
                                                type="text"
                                                value={skill.name}
                                                onChange={(e) => {
                                                    const newSkills = [...profileForm.skills];
                                                    newSkills[index] = { ...skill, name: e.target.value };
                                                    setProfileForm(prev => ({ ...prev, skills: newSkills }));
                                                }}
                                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                                placeholder="技能名称"
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={skill.level}
                                                onChange={(e) => {
                                                    const newSkills = [...profileForm.skills];
                                                    newSkills[index] = { ...skill, level: parseInt(e.target.value) || 0 };
                                                    setProfileForm(prev => ({ ...prev, skills: newSkills }));
                                                }}
                                                className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                                placeholder="熟练度"
                                            />
                                            <input
                                                type="text"
                                                value={skill.category}
                                                onChange={(e) => {
                                                    const newSkills = [...profileForm.skills];
                                                    newSkills[index] = { ...skill, category: e.target.value };
                                                    setProfileForm(prev => ({ ...prev, skills: newSkills }));
                                                }}
                                                className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                                placeholder="分类"
                                            />
                                            <button
                                                onClick={() => {
                                                    const newSkills = profileForm.skills.filter((_, i) => i !== index);
                                                    setProfileForm(prev => ({ ...prev, skills: newSkills }));
                                                }}
                                                className="px-3 py-2 text-red-600 hover:text-red-800"
                                            >
                                                删除
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        onClick={() => {
                                            setProfileForm(prev => ({
                                                ...prev,
                                                skills: [...prev.skills, { name: '', level: 50, category: '' }]
                                            }));
                                        }}
                                        className="px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
                                    >
                                        + 添加技能
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'stats' && (
                    <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold">文章总数</h3>
                                <FileText className="w-6 h-6 text-blue-500" />
                            </div>
                            <div className="text-4xl font-bold">{stats?.totalPosts ?? posts.length}</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold">已发布</h3>
                                <Eye className="w-6 h-6 text-emerald-500" />
                            </div>
                            <div className="text-4xl font-bold">{stats?.publishedPosts ?? posts.filter((item) => item.published).length}</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold">总访问量</h3>
                                <BarChart2 className="w-6 h-6 text-indigo-500" />
                            </div>
                            <div className="text-4xl font-bold">{stats?.totalViews ?? 0}</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold">今日访问</h3>
                                <Clock className="w-6 h-6 text-orange-500" />
                            </div>
                            <div className="text-4xl font-bold">{stats?.viewsToday ?? 0}</div>
                        </div>
                    </div>
                )}

                {/* 隐藏的文件输入 */}
                <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={handleImageUpload}
                />
                <input
                    ref={profileImageInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={handleProfileImageUpload}
                />
            </div>
        </div>
    );
};

export default Admin;
